/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { ReactNode } from "react";
import { createPortal } from "react-dom";
import ReactTooltip, { Place } from "react-tooltip";
import styled, { useTheme } from "styled-components";

interface TooltipWrapperProps {
  id?: string;
  color?: string;
  icon: ReactNode;
  content: ReactNode;
  direction?: Place;
  eventDelay?: number;
  padding?: number;
  isScrolling?: boolean;
}

const TooltipPortal = ({ children }: { children: ReactNode }) =>
  createPortal(children, document.body);

const StyledReactTooltip = styled(ReactTooltip)<{
  eventDelay?: number;
  padding: number;
}>`
  border-radius: 10px !important;
  padding: ${({ padding }) => padding}px !important;
  border: none !important;

  opacity: 0 !important;
  transition: opacity 0.3s ease-out, visibility 0.3s ease-out !important;
  color: ${({ theme }) => theme.globalConstants.color.white} !important;
  width: fit-content !important;
  max-width: 200px !important;

  &.__react_component_tooltip.show {
    opacity: 1 !important;
  }

  &.place-left::before,
  &.place-right::before,
  &.place-top::before,
  &.place-bottom::before {
    display: none !important;
  }

  &.place-left::after,
  &.place-right::after {
    border-top-width: 10px !important;
    border-bottom-width: 10px !important;
    margin-top: -10px !important;
  }

  &.place-left::after {
    border-left-width: 8px !important;
    right: -7px !important;
  }

  &.place-right::after {
    border-right-width: 8px !important;
    left: -7px !important;
  }

  &.place-top::after,
  &.place-bottom::after {
    border-left-width: 10px !important;
    border-right-width: 10px !important;
    margin-left: -10px !important;
  }

  &.place-top::after {
    border-top-width: 8px !important;
    bottom: -7px !important;
  }

  &.place-bottom::after {
    border-bottom-width: 8px !important;
    top: -7px !important;
  }
`;

const TooltipWrapper: React.FC<TooltipWrapperProps> = ({
  color = "backgroundLight",
  content,
  direction = "right",
  eventDelay,
  icon,
  id = "Tooltip",
  padding = 24,
}) => {
  const { globalConstants } = useTheme();
  return (
    <>
      <span data-tip="" data-for={id} style={{ cursor: "pointer" }}>
        {icon}
      </span>
      <TooltipPortal>
        <StyledReactTooltip
          backgroundColor={globalConstants.color[color]}
          border={false}
          clickable={true}
          delayHide={eventDelay ? eventDelay * 1000 : 0}
          effect="solid"
          eventDelay={eventDelay}
          id={id}
          place={direction}
          padding={padding}
        >
          {content}
        </StyledReactTooltip>
      </TooltipPortal>
    </>
  );
};

export default TooltipWrapper;
